import React from 'react';
import { Box } from '@mui/material';

import Text from 'components/text';
import InvitedList from './invited-list';
import NewInviteForm from './new-invite-form';
import { useUserData } from 'hooks/useUserData';
import { SongFundTabs, SongFundTab } from 'components/songfund-tabs';
import AdminReports from './admin-reports';
import Renewals from './Renewals';
import CollabClub from './collab-club';
import PublishingAdmin from './publishing';
import MarketingAdmin from './marketing';
import { useHistory, useLocation } from 'react-router-dom';

/** Tab slugs, in display order; `/admin?tab=<slug>` deep-links to a tab (e.g. the publishing notification mail). */
const ADMIN_TABS = [
  'reports',
  'invited',
  'invite',
  'renewals',
  'collab',
  'publishing',
  'marketing',
] as const;

const Admin: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const tabFromUrl = Math.max(
    0,
    ADMIN_TABS.indexOf(
      new URLSearchParams(location.search).get('tab') as (typeof ADMIN_TABS)[number]
    )
  );
  const [selectedTab, setSelectedTab] = React.useState(tabFromUrl);

  // Keep the tab in sync with back/forward navigation.
  React.useEffect(() => {
    setSelectedTab(tabFromUrl);
  }, [tabFromUrl]);

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    history.replace({ pathname: location.pathname, search: `?tab=${ADMIN_TABS[newValue]}` });
  };

  const { permissionsData } = useUserData();

  if (!permissionsData?.admin) {
    return (
      <Box padding={3}>
        <Text>
          Sorry, the page you are looking for does not exist, please contact with us if you are
          facing an issues.
        </Text>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" flexGrow={1} minWidth={0} minHeight={0}>
      <Box
        maxWidth="inherit"
        overflow="auto"
        sx={{
          maxWidth: '100vw',
        }}
      >
        <Box minWidth="500px" marginX="auto">
          <SongFundTabs
            value={selectedTab}
            onChange={handleChangeTab}
            centered
            aria-label="admin tabs"
          >
            <SongFundTab label="Reports" />
            <SongFundTab label="Invited Artists" />
            <SongFundTab label="New Invitation" />
            <SongFundTab label="Renewals" />
            <SongFundTab label="Collab Admin" />
            <SongFundTab label="Publishing" />
            <SongFundTab
              label="Marketing"
              // Re-click while open: clearing the hash sends the embedded app back to the plan list.
              onClick={() => selectedTab === 6 && history.replace({ ...location, hash: '' })}
            />
          </SongFundTabs>
        </Box>
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        flexGrow={1}
        minWidth={0}
        minHeight={0}
        sx={{ overflowX: 'auto' }}
      >
        {selectedTab === 0 && <AdminReports />}
        {selectedTab === 1 && <InvitedList />}
        {selectedTab === 2 && <NewInviteForm />}
        {selectedTab === 3 && <Renewals />}
        {/* Fill the remaining height and scroll internally (so the page/sidebar
            don't scroll) — flex-fill instead of a fixed calc to avoid dead space. */}
        {selectedTab === 4 && (
          <Box flex={1} minWidth={0} minHeight={0} sx={{ overflow: 'auto' }}>
            <CollabClub />
          </Box>
        )}
        {selectedTab === 5 && (
          <Box flex={1} minWidth={0} minHeight={0} sx={{ overflow: 'auto' }}>
            <PublishingAdmin />
          </Box>
        )}
        {selectedTab === 6 && (
          <Box flex={1} minWidth={0} minHeight={0} display="flex">
            <MarketingAdmin />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Admin;
